declare global {
	interface Window {
		grecaptcha?: {
			ready: (cb: () => void) => void;
			execute: (captchaKey: string, opts: { action: 'submit' }) => Promise<string | undefined>;
		};
	}
}

function resolveSiteKey(siteKey?: string): string | undefined {
	return siteKey ?? import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY;
}

export type GenerateCaptchaTokenError = 'INVALID_CAPTCHA' | 'CAPTCHA_NOT_LOADED' | 'INVALID_CAPTCHA_KEY_OR_ORIGIN';

type GenerateCaptchaTokenResult = { data: string; error: null } | { data: null; error: GenerateCaptchaTokenError };

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';
// A `defer`red third-party script on a cold/slow connection can take several seconds to load and run.
// The old mechanism polled for `window.grecaptcha` only 6×50 ms (300 ms) before giving up, which is the
// main source of spurious "[WARN] Google Recaptcha not loaded" (TEC-3418). Wait on the script's real
// load/ready events instead, capped by this generous timeout so a blocked/hung load fails open rather
// than leaving every donation submit awaiting forever.
const RECAPTCHA_READY_TIMEOUT_MS = 10_000;

/**
 * A single in-flight/settled load promise, keyed by the site key it was started for. Concurrent callers
 * (a form-mount warm-up plus the actual submit) share it instead of each injecting a script and racing
 * their own poll. If the resolved key ever changes we start a fresh load rather than reuse a promise
 * bound to the old key.
 */
let loadState: { key: string; promise: Promise<boolean> } | undefined;

/**
 * Inject Google reCAPTCHA v3 `api.js?render=<key>` (once) and resolve when `grecaptcha` is actually
 * ready to mint tokens. Resolves `true` when ready, `false` if the script fails to load / is blocked /
 * times out — callers turn `false` into CAPTCHA_NOT_LOADED. Reliability guarantees:
 *
 * - Rendered with the SAME key the caller passes to `execute`, so we never trip reCAPTCHA's
 *   "Invalid site key or not loaded in api.js" key-mismatch.
 * - Reuses a pre-existing api.js (whether ours by id, another script by src, or an already-present
 *   `window.grecaptcha` from an SSR-injected tag / standalone-embed host) instead of double-loading.
 * - Settles via `grecaptcha.ready()` (fires only once the render key is registered), never a blind poll.
 */
export function loadRecaptcha(siteKey?: string): Promise<boolean> {
	const key = resolveSiteKey(siteKey);
	if (typeof window === 'undefined' || typeof document === 'undefined' || !key) return Promise.resolve(false);
	if (loadState && loadState.key === key) return loadState.promise;

	const promise = new Promise<boolean>((resolve) => {
		let settled = false;
		const finish = (ready: boolean) => {
			if (settled) return;
			settled = true;
			window.clearTimeout(timer);
			// Cache only a successful load. A `false` result (timeout / blocked / error) is evicted so the
			// next caller re-attempts instead of being pinned to the failure — api.js often finishes just
			// after our timeout, and the retry then lands.
			if (!ready && loadState?.key === key) loadState = undefined;
			resolve(ready);
		};

		const timer = window.setTimeout(() => finish(false), RECAPTCHA_READY_TIMEOUT_MS);

		// `grecaptcha` can be assigned as a partial stub before `ready`/`execute` are wired up, so guard
		// both. `ready()` queues the callback until the render key is fully registered.
		const settleWhenReady = () => {
			const grecaptcha = window.grecaptcha;
			if (grecaptcha && typeof grecaptcha.ready === 'function') {
				grecaptcha.ready(() => finish(typeof window.grecaptcha?.execute === 'function'));
			} else {
				finish(false);
			}
		};

		// Already loaded (SSR-injected tag, a sibling app, or a standalone-embed host) — just wait for ready.
		if (window.grecaptcha) {
			settleWhenReady();
			return;
		}

		// A script tag is present but hasn't finished — attach to it rather than inject a duplicate.
		const existing = document.querySelector<HTMLScriptElement>('script[src*="recaptcha/api.js"]');
		if (existing) {
			existing.addEventListener('load', settleWhenReady, { once: true });
			existing.addEventListener('error', () => finish(false), { once: true });
			return;
		}

		const script = document.createElement('script');
		script.id = RECAPTCHA_SCRIPT_ID;
		script.src = `https://www.google.com/recaptcha/api.js?render=${key}`;
		script.defer = true;
		script.addEventListener('load', settleWhenReady, { once: true });
		script.addEventListener('error', () => finish(false), { once: true });
		document.head.appendChild(script);
	});

	loadState = { key, promise };
	return promise;
}

export async function generateCaptchaTokenResult(siteKey?: string): Promise<GenerateCaptchaTokenResult> {
	if (typeof window === 'undefined') return { data: null, error: 'CAPTCHA_NOT_LOADED' };

	const key = resolveSiteKey(siteKey);
	if (key === undefined) return { data: null, error: 'INVALID_CAPTCHA_KEY_OR_ORIGIN' };

	// Ensure api.js is loaded and ready for THIS key before executing. Self-loading (rather than
	// depending on a form having been mounted) is what lets the donate flow generate tokens reliably.
	const ready = await loadRecaptcha(key);
	const grecaptcha = window.grecaptcha;
	if (!ready || !grecaptcha || typeof grecaptcha.execute !== 'function') {
		return { data: null, error: 'CAPTCHA_NOT_LOADED' };
	}

	return new Promise<GenerateCaptchaTokenResult>((resolve) => {
		grecaptcha.ready(() => {
			grecaptcha
				.execute(key, { action: 'submit' })
				.then((captchaToken) => {
					resolve(captchaToken ? { data: captchaToken, error: null } : { data: null, error: 'INVALID_CAPTCHA' });
				})
				// execute() rejects with "Invalid site key or not loaded in api.js" when the key isn't
				// registered for this instance/origin. Surface a typed result instead of an uncaught rejection.
				.catch(() => resolve({ data: null, error: 'INVALID_CAPTCHA' }));
		});
	});
}
